import React, { createContext, useContext, useState, ReactNode } from 'react';
import axios from 'axios';
import { getEndpoint, logEnvironment } from '../utils/endpoints';


type AuthContextType = {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthProviderProps = {
  children: ReactNode;
};


export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize authentication state from localStorage token presence
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    try {
      return !!localStorage.getItem('token');
    } catch (e) {
      return false;
    }
  });

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const loginEndpoint = getEndpoint('LOGIN');
      logEnvironment();
  
      const response = await axios.post(
        `${loginEndpoint}/login`,
        { username, password },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
  
      if (response.status === 200) {
        // Try to extract a token from common response shapes
        const token =
          response.data?.token ||
          response.data?.access_token ||
          response.data?.key ||
          (response.headers && response.headers.authorization
            ? String(response.headers.authorization).replace(/^Bearer\s+/i, '')
            : undefined);

        try {
          if (token) {
            localStorage.setItem('token', token);
          }
        } catch (e) {
          console.warn('Unable to persist token to localStorage', e);
        }

        setIsAuthenticated(true); // Imposta l'autenticazione
        return true;
      }
  
      console.warn('Login failed: Unexpected server response', response);
      return false;
    } catch (error) {
      console.error('Error logging in:', error);
      return false;
    }
  };


  const logout = () => {
    try {
      localStorage.removeItem('token');
    } catch (e) {
      console.error('AuthContext: error removing token:', e);
    }
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
